import { motion } from "framer-motion";
import {
  ArrowRight,
  BellRing,
  BookOpenCheck,
  CalendarClock,
  Check,
  FileSearch,
  GanttChartSquare,
  MailCheck,
  Radar,
  SearchCheck,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { getTranslationFn } from "../../i18n";
import type { Locale } from "../../lib/locale";
import * as Paths from "../../paths";
import {
  SejmMonitorReportDetail,
  type SejmMonitorReportDetailData,
} from "../sejm-monitor-report-detail";
import { Button } from "../ui/button";
import { MarketingLayout } from "./marketing-layout";

interface MonitoringPageProps {
  locale: Locale;
}

type TranslationKey = Parameters<ReturnType<typeof getTranslationFn>>[0];

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function getDemoReport(
  t: ReturnType<typeof getTranslationFn>
): SejmMonitorReportDetailData {
  return {
    id: "demo-report",
    monitorId: "demo-monitor",
    monitorPhrase: t("Monitoring.preview.monitor1"),
    windowStart: "2026-02-20T00:00:00.000Z",
    windowEnd: "2026-02-21T00:00:00.000Z",
    sourceActCount: 42,
    matchedActCount: 3,
    matchedItemCount: 7,
    createdAt: "2026-02-21T08:00:00.000Z",
    summaryMarkdown: "",
    summaryItems: [
      {
        changeSummary: t("Monitoring.preview.match1.detail"),
        provisionName: t("Monitoring.preview.match1.actTitle"),
        officialSourceUrl: "https://dziennikustaw.gov.pl/DU/2026/318",
        actDetails: {
          title: t("Monitoring.preview.match1.actTitle"),
          citation: t("Monitoring.preview.match1.source"),
          actPublisherCode: "DU",
          actYear: 2026,
          actPos: 318,
          eventDate: "2026-02-20",
          eventKind: "announcement",
          changeDate: null,
          announcementDate: "2026-02-20",
          entryIntoForce: "2026-03-01",
          expirationDate: null,
          legalStatusDate: "2026-02-20",
          promulgation: "2026-02-20",
          repealDate: null,
          validFrom: "2026-03-01",
          actStatus: t("New act"),
          actInForce: null,
          processNote: null,
        },
      },
      {
        changeSummary: t("Monitoring.preview.match2.detail"),
        provisionName: t("Monitoring.preview.match2.actTitle"),
        officialSourceUrl: "https://dziennikustaw.gov.pl/DU/2026/412",
        actDetails: {
          title: t("Monitoring.preview.match2.actTitle"),
          citation: t("Monitoring.preview.match2.source"),
          actPublisherCode: "DU",
          actYear: 2026,
          actPos: 412,
          eventDate: "2026-02-20",
          eventKind: "change",
          changeDate: "2026-02-20",
          announcementDate: null,
          entryIntoForce: "2026-04-01",
          expirationDate: null,
          legalStatusDate: "2026-02-20",
          promulgation: "2026-02-20",
          repealDate: null,
          validFrom: "2026-04-01",
          actStatus: t("Updated act"),
          actInForce: null,
          processNote: t("Monitoring.preview.match2.source"),
        },
      },
      {
        changeSummary: t("Monitoring.preview.match3.detail"),
        provisionName: t("Monitoring.preview.match3.actTitle"),
        officialSourceUrl: "https://dziennikustaw.gov.pl/DU/2026/477",
        actDetails: {
          title: t("Monitoring.preview.match3.actTitle"),
          citation: t("Monitoring.preview.match3.source"),
          actPublisherCode: "DU",
          actYear: 2026,
          actPos: 477,
          eventDate: "2026-02-20",
          eventKind: "change",
          changeDate: "2026-02-20",
          announcementDate: null,
          entryIntoForce: "2026-03-15",
          expirationDate: null,
          legalStatusDate: "2026-02-20",
          promulgation: "2026-02-20",
          repealDate: null,
          validFrom: "2026-03-15",
          actStatus: t("Updated act"),
          actInForce: null,
          processNote: null,
        },
      },
    ],
    items: [
      {
        id: "demo-item-1",
        monitorPhrase: t("Monitoring.preview.monitor1"),
        matchField: "text",
        matchExcerpt: t("Monitoring.preview.match1.detail"),
        isNew: true,
        actId: "demo-act-1",
        actTitle: t("Monitoring.preview.match1.actTitle"),
        actDisplayAddress: t("Monitoring.preview.match1.source"),
        actPublisherCode: "DU",
        actYear: 2026,
        actPos: 318,
        actAnnouncementDate: "2026-02-20",
        actChangeDate: null,
        actEntryIntoForce: "2026-03-01",
        actExpirationDate: null,
        actLegalStatusDate: "2026-02-20",
        actPromulgation: "2026-02-20",
        actRepealDate: null,
        actValidFrom: "2026-03-01",
        actStatus: t("New act"),
        actInForce: null,
        processNote: null,
        processLink: null,
      },
      {
        id: "demo-item-2",
        monitorPhrase: t("Monitoring.preview.monitor2"),
        matchField: "text",
        matchExcerpt: t("Monitoring.preview.match2.detail"),
        isNew: false,
        actId: "demo-act-2",
        actTitle: t("Monitoring.preview.match2.actTitle"),
        actDisplayAddress: t("Monitoring.preview.match2.source"),
        actPublisherCode: "DU",
        actYear: 2026,
        actPos: 412,
        actAnnouncementDate: null,
        actChangeDate: "2026-02-20",
        actEntryIntoForce: "2026-04-01",
        actExpirationDate: null,
        actLegalStatusDate: "2026-02-20",
        actPromulgation: "2026-02-20",
        actRepealDate: null,
        actValidFrom: "2026-04-01",
        actStatus: t("Updated act"),
        actInForce: null,
        processNote: t("Monitoring.preview.match2.source"),
        processLink: null,
      },
      {
        id: "demo-item-3",
        monitorPhrase: t("Monitoring.preview.monitor3"),
        matchField: "text",
        matchExcerpt: t("Monitoring.preview.match3.detail"),
        isNew: false,
        actId: "demo-act-3",
        actTitle: t("Monitoring.preview.match3.actTitle"),
        actDisplayAddress: t("Monitoring.preview.match3.source"),
        actPublisherCode: "DU",
        actYear: 2026,
        actPos: 477,
        actAnnouncementDate: null,
        actChangeDate: "2026-02-20",
        actEntryIntoForce: "2026-03-15",
        actExpirationDate: null,
        actLegalStatusDate: "2026-02-20",
        actPromulgation: "2026-02-20",
        actRepealDate: null,
        actValidFrom: "2026-03-15",
        actStatus: t("Updated act"),
        actInForce: null,
        processNote: null,
        processLink: null,
      },
    ],
  };
}

function MonitoringReportPreview({ locale }: { locale: Locale }) {
  const t = getTranslationFn(locale);
  const report = getDemoReport(t);

  return (
    <div className="relative mx-auto w-full max-w-5xl rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl shadow-slate-950/15">
      <div className="max-h-[540px] overflow-hidden rounded-xl bg-slate-50 p-4">
        <SejmMonitorReportDetail
          report={report}
          translate={t}
          showHits={false}
          className="[&_.rounded-lg]:rounded-md [&_.shadow-sm]:shadow-none"
        />
      </div>
    </div>
  );
}

export function MonitoringPage({ locale }: MonitoringPageProps) {
  const t = getTranslationFn(locale);

  const navItems = [
    { label: t("Monitoring.nav.report"), href: "#report" },
    { label: t("Monitoring.nav.workflow"), href: "#workflow" },
    { label: t("Monitoring.nav.coverage"), href: "#coverage" },
    { label: t("Monitoring.nav.faq"), href: "#faq" },
  ];

  const benefits = [
    {
      icon: BellRing,
      title: t("Monitoring.benefit1.title"),
      body: t("Monitoring.benefit1.body"),
    },
    {
      icon: SearchCheck,
      title: t("Monitoring.benefit2.title"),
      body: t("Monitoring.benefit2.body"),
    },
    {
      icon: MailCheck,
      title: t("Monitoring.benefit3.title"),
      body: t("Monitoring.benefit3.body"),
    },
  ];

  const workflow = [
    {
      icon: Radar,
      title: t("Monitoring.workflow1.title"),
      body: t("Monitoring.workflow1.body"),
    },
    {
      icon: GanttChartSquare,
      title: t("Monitoring.workflow2.title"),
      body: t("Monitoring.workflow2.body"),
    },
    {
      icon: Sparkles,
      title: t("Monitoring.workflow3.title"),
      body: t("Monitoring.workflow3.body"),
    },
  ];

  const coverage = [
    "Monitoring.coverage1",
    "Monitoring.coverage2",
    "Monitoring.coverage3",
    "Monitoring.coverage4",
    "Monitoring.coverage5",
    "Monitoring.coverage6",
  ];

  const faq = [
    ["Monitoring.faq1.q", "Monitoring.faq1.a"],
    ["Monitoring.faq2.q", "Monitoring.faq2.a"],
    ["Monitoring.faq3.q", "Monitoring.faq3.a"],
  ];

  return (
    <MarketingLayout locale={locale} navItems={navItems}>
      <section className="relative overflow-hidden bg-[#f7faf8] pt-8">
        <div className="absolute inset-0 bg-[linear-gradient(115deg,rgba(14,89,56,0.08)_0%,rgba(255,255,255,0)_35%),linear-gradient(180deg,#f7faf8_0%,#ffffff_100%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-300 to-transparent" />
        <div className="relative container mx-auto px-4 md:px-6">
          <div className="grid gap-8 pb-8 pt-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:pt-8">
            <div className="max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-brand-700 shadow-sm shadow-brand-900/5"
              >
                <CalendarClock className="h-4 w-4" />
                {t("Monitoring.hero.badge")}
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.68,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.06,
                }}
                className="text-4xl font-semibold leading-[0.98] tracking-tight text-slate-950 sm:text-5xl md:text-6xl"
              >
                {t("Monitoring.hero.title")}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.68,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.14,
                }}
                className="mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg"
              >
                {t("Monitoring.hero.description")}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.68,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.22,
                }}
                className="mt-7 flex flex-col gap-3 sm:flex-row"
              >
                <Button
                  asChild
                  size="lg"
                  className="h-14 rounded-full bg-brand-600 px-7 text-white shadow-xl shadow-brand-700/20 hover:bg-brand-700"
                >
                  <Link to={Paths.eliMonitors()}>
                    {t("Monitoring.hero.primaryCta")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 rounded-full border-slate-300 bg-white/80 px-7 text-slate-800 hover:bg-white"
                >
                  <Link to="mailto:hello@anylawyer.com">
                    {t("Monitoring.hero.secondaryCta")}
                  </Link>
                </Button>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.16,
              }}
              className="relative hidden h-[480px] overflow-visible lg:-mr-4 lg:block"
            >
              <div className="origin-top-left scale-[0.82] xl:scale-[0.86]">
                <MonitoringReportPreview locale={locale} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="report" className="bg-white pt-10 pb-20 md:pb-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-600">
              {t("Monitoring.report.eyebrow")}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              {t("Monitoring.report.title")}
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600">
              {t("Monitoring.report.description")}
            </p>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {benefits.map((benefit, index) => (
              <Reveal key={benefit.title} delay={index * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:shadow-slate-900/10">
                  <benefit.icon className="h-7 w-7 text-brand-600" />
                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-slate-950">
                    {benefit.title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">
                    {benefit.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        id="workflow"
        className="border-y border-slate-200 bg-slate-950 py-20 text-white md:py-28"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <Reveal>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-300">
                {t("Monitoring.workflow.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                {t("Monitoring.workflow.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                {t("Monitoring.workflow.description")}
              </p>
            </Reveal>

            <div className="space-y-4">
              {workflow.map((item, index) => (
                <Reveal key={item.title} delay={index * 0.08}>
                  <div className="grid gap-5 rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur sm:grid-cols-[3rem_1fr]">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-300/15 text-brand-200">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-brand-200">
                        {t("Monitoring.workflow.step", {
                          number: String(index + 1),
                        })}
                      </div>
                      <h3 className="mt-1 text-xl font-semibold tracking-tight">
                        {item.title}
                      </h3>
                      <p className="mt-2 leading-7 text-slate-300">
                        {item.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="coverage" className="bg-[#f7faf8] py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <Reveal>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-brand-600">
                {t("Monitoring.coverage.eyebrow")}
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                {t("Monitoring.coverage.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                {t("Monitoring.coverage.description")}
              </p>
            </Reveal>

            <Reveal>
              <div className="grid gap-3 sm:grid-cols-2">
                {coverage.map((key) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                    <span className="text-sm leading-6 text-slate-700">
                      {t(key as TranslationKey)}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <BookOpenCheck className="h-8 w-8 text-brand-600" />
              <h2 className="mt-5 text-3xl font-semibold tracking-tight text-slate-950 md:text-5xl">
                {t("Monitoring.faq.title")}
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                {t("Monitoring.faq.description")}
              </p>
            </Reveal>

            <div className="space-y-4">
              {faq.map(([questionKey, answerKey], index) => (
                <Reveal key={questionKey} delay={index * 0.08}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-lg font-semibold tracking-tight text-slate-950">
                      {t(questionKey as TranslationKey)}
                    </h3>
                    <p className="mt-3 leading-7 text-slate-600">
                      {t(answerKey as TranslationKey)}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-900 py-20 md:py-28">
        <div className="container mx-auto px-4 text-center md:px-6">
          <FileSearch className="mx-auto h-9 w-9 text-brand-200" />
          <h2 className="mx-auto mt-6 max-w-3xl text-3xl font-semibold tracking-tight text-white md:text-5xl">
            {t("Monitoring.cta.title")}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-brand-100/75">
            {t("Monitoring.cta.description")}
          </p>
          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="h-14 rounded-full bg-white px-8 text-brand-900 shadow-2xl shadow-black/20 hover:bg-brand-50"
            >
              <Link to={Paths.eliMonitors()}>
                {t("Monitoring.cta.primary")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="h-14 rounded-full border-brand-200/40 bg-transparent px-8 text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="mailto:hello@anylawyer.com">
                {t("Monitoring.cta.secondary")}
              </Link>
            </Button>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 text-sm text-brand-100/70">
            <ShieldCheck className="h-4 w-4" />
            {t("Monitoring.cta.security")}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
